import { useState, useEffect, useCallback, useRef } from 'react'
import { channelApi, categoriesApi } from '../api'
import { Card, Modal } from '../components/UI'
import { Beacon, Switch, ConfirmModal, Loader } from '../ui'
import { useStore } from '../store'
import { Spinner, ErrorBox, EmptyState } from '../hooks/useApi.jsx'
import {
  HiCircleStack, HiArrowPath, HiVideoCamera, HiPhoto, HiDocument, HiSparkles, HiCheck, HiPlus,
  HiForward, HiEye, HiChevronLeft, HiChevronRight, HiMusicalNote,
} from 'react-icons/hi2'

// Media turi — bosqich emas, shoshilinchlik ham emas: rang tashimaydi (DESIGN.md 1).
// Turni ikonka va yorliq ajratadi.
const MEDIA_BADGE = {
  video: { icon: HiVideoCamera, label: 'Video' },
  photo: { icon: HiPhoto, label: 'Rasm' },
  document: { icon: HiDocument, label: 'Hujjat' },
  audio: { icon: HiMusicalNote, label: 'Audio' },
  none: { icon: HiDocument, label: 'Matn' },
}

function fmtDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export default function ChannelBase() {
  const [stats, setStats] = useState(null)
  const [sync, setSync] = useState(null)
  const [posts, setPosts] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [media, setMedia] = useState('')
  const [onlyUnfaq, setOnlyUnfaq] = useState(false)
  const [page, setPage] = useState(1)
  const PER = 20
  // FAQ modal
  const [faqModal, setFaqModal] = useState(null)   // post obj
  const [question, setQuestion] = useState('')
  const [catId, setCatId] = useState('')
  const [cats, setCats] = useState([])
  const [suggests, setSuggests] = useState([])
  const [suggesting, setSuggesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const searchTimer = useRef(null)
  const [clsStatus, setClsStatus] = useState(null)
  const [auto, setAuto] = useState(false)
  // Tasdiq oynalari: amal darhol bajarilmaydi, avval so'raladi
  const [askClassify, setAskClassify] = useState(false)
  const [askFullSync, setAskFullSync] = useState(false)
  const addToast = useStore(s => s.addToast)

  const refreshCls = useCallback(async () => {
    try {
      const [cs, au] = await Promise.all([channelApi.classifyStatus(), channelApi.getAuto()])
      setClsStatus(cs); setAuto(!!au.enabled)
    } catch {}
  }, [])

  const startClassify = async () => {
    try {
      const r = await channelApi.classifyStart(true)
      if (r.ok === false) addToast(r.error || "Saralashni boshlab bo'lmadi", 'critical')
      refreshCls()
    } catch (e) { addToast(e.message || "Saralashni boshlab bo'lmadi", 'critical') }
  }
  const cancelClassify = async () => { try { await channelApi.classifyCancel(); refreshCls() } catch {} }
  const toggleAuto = async () => {
    try { const r = await channelApi.setAuto(!auto); setAuto(r.enabled) }
    catch (e) { addToast(e.message || "AVTO rejimni o'zgartirib bo'lmadi", 'critical') }
  }

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const params = { skip: (page - 1) * PER, limit: PER }
      if (search) params.search = search
      if (media) params.media = media
      if (onlyUnfaq) params.only_unfaq = true
      const [st, ps, sy] = await Promise.all([
        channelApi.stats(), channelApi.posts(params), channelApi.syncStatus(),
      ])
      setStats(st); setPosts(ps); setSync(sy)
    } catch (e) { setError(e.message) }
    finally { if (!silent) setLoading(false) }
  }, [page, search, media, onlyUnfaq])

  useEffect(() => { load() }, [load])
  useEffect(() => { refreshCls() }, [refreshCls])
  useEffect(() => {
    const id = setInterval(() => {
      channelApi.syncStatus().then(s => { setSync(s); if (s.running) load(true) }).catch(() => {})
      channelApi.classifyStatus().then(cs => {
        setClsStatus(cs)
        if (cs.running) load(true)
      }).catch(() => {})
    }, 3000)
    return () => clearInterval(id)
  }, [load])

  const startSync = async (mode) => {
    try {
      const r = await channelApi.startSync('jizzax_kiber', mode)
      if (r.ok === false && r.error) addToast(r.error, 'critical')
      load(true)
    } catch (e) { addToast(e.message || "Sinxronizatsiyani boshlab bo'lmadi", 'critical') }
  }

  const openFaqModal = async (post) => {
    setFaqModal(post)
    setQuestion((post.text || '').split('\n')[0].slice(0, 120))
    setSuggests([]); setCatId('')
    if (!cats.length) categoriesApi.list().then(setCats).catch(() => {})
  }

  const askAi = async () => {
    if (!faqModal) return
    setSuggesting(true)
    try {
      const r = await channelApi.suggestQuestion(faqModal.id)
      if (r.ok) setSuggests(r.questions || [])
      else addToast(r.error || 'AI bu post uchun savol topa olmadi', 'default')
    } catch (e) { addToast(e.message || "AI taklifini olib bo'lmadi", 'critical') }
    finally { setSuggesting(false) }
  }

  const saveFaq = async () => {
    if (!faqModal || !question.trim()) return
    setSaving(true)
    try {
      await channelApi.toFaq(faqModal.id, { question_uz: question.trim(), category_id: catId || undefined })
      setFaqModal(null)
      load(true)
    } catch (e) { addToast(e.message || "Postni FAQ ga qo'shib bo'lmadi", 'critical') }
    finally { setSaving(false) }
  }

  const totalPages = posts ? Math.max(1, Math.ceil(posts.total / PER)) : 1

  return (
    <div className="page-enter">
      <div className="page-hdr">
        <div className="page-hdr-left"><h2>Kanal bazasi</h2><p>t.me/jizzax_kiber — to'liq tarix (bilim bazasi manbai)</p></div>
        <div className="page-hdr-right" style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => startSync('new')} disabled={sync?.running}>
            <HiArrowPath /> Yangi postlarni olish
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setAskFullSync(true)} disabled={sync?.running}>
            To'liq qayta yuklash
          </button>
        </div>
      </div>

      <ErrorBox error={error} onRetry={load} />

      {/* Sinxronizatsiya holati */}
      {sync && (sync.running || sync.error || sync.fetched > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 16,
          borderRadius: 'var(--r-lg)',
          border: `1px solid ${sync.error ? 'var(--ui-st-critical)' : sync.running ? 'var(--border-2)' : 'var(--ui-st-ok)'}`,
          background: 'var(--surface)', fontSize: 13 }}>
          {sync.running
            // Sinxronizatsiya ishlab turibdi: kanal tarixi o'qilib, postlar
            // qayta ishlanmoqda — bu hisoblash/tahlil kutishi, ya'ni "solving".
            // Matn yonida allaqachon yozilgani uchun label={false}.
            ? <Loader state="solving" size="sm" label={false} />
            : sync.error
              ? <Beacon aria-hidden="true" />
              : <HiCheck aria-hidden="true" style={{ color: 'var(--ui-st-ok)' }} />}
          <div style={{ flex: 1 }}>
            {sync.running
              ? <>Yig'ilmoqda: <b>{sync.fetched}</b> ta post o'qildi (yangi: {sync.added}, yangilangan: {sync.updated})…</>
              : sync.error
                ? <span style={{ color: 'var(--ui-st-critical)' }}>Xato: {sync.error}</span>
                : <>Oxirgi sinxronizatsiya: <b>{sync.fetched}</b> post (yangi: {sync.added}, yangilangan: {sync.updated})</>}
          </div>
        </div>
      )}

      {/* AI ommaviy saralash + AVTO rejim + eksport */}
      <Card style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 14 }}>
              <HiSparkles aria-hidden="true" style={{ color: 'var(--text-1)' }} /> AI bilan avtomatik saralash
            </div>
            <span style={{ marginLeft: 'auto' }} />
            {clsStatus?.running ? (
              <button className="btn btn-danger btn-sm" onClick={cancelClassify}>To'xtatish</button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={() => setAskClassify(true)}>
                <HiSparkles size={14} /> Ko'rilmaganlarni AI saralasin
              </button>
            )}
            <a className="btn btn-secondary btn-sm" href={channelApi.exportUrl()} target="_blank" rel="noreferrer">JSON yuklab olish</a>
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8, lineHeight: 1.55 }}>
            AI har bir postni (izoh + rasm) ko'rib chiqadi: foydali bo'lsa — savol-teg
            yozib avtomatik FAQ ga qo'shadi, reklama/takror bo'lsa — o'tkazib yuboradi.
            Videolarni izohi bo'yicha baholaydi.
          </div>

          {clsStatus && (clsStatus.running || clsStatus.done > 0) && (
            <div style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 5 }}>
                <span>{clsStatus.running ? 'Saralanmoqda…' : 'Saralash yakunlandi'} — {clsStatus.done}/{clsStatus.total}</span>
                <span style={{ color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <HiCheck size={12} aria-hidden="true" /> FAQ: {clsStatus.faq}
                  </span>
                  ·
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <HiForward size={12} aria-hidden="true" /> o'tkazildi: {clsStatus.skipped}
                  </span>
                  {clsStatus.errors > 0 && (
                    <>
                      ·
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--ui-st-critical)' }}>
                        <Beacon size={12} tone="inherit" aria-hidden="true" /> xato: {clsStatus.errors}
                      </span>
                    </>
                  )}
                  · ${(clsStatus.cost_usd || 0).toFixed(3)}
                </span>
              </div>
              <div style={{ height: 8, borderRadius: 'var(--r)', background: 'var(--surface-2)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${clsStatus.total ? (clsStatus.done / clsStatus.total * 100) : 0}%`,
                  background: 'var(--text-1)', transition: 'width var(--dur-1) var(--ease)' }} />
              </div>
              {clsStatus.running && clsStatus.current && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, fontFamily: 'var(--mono)' }}>hozir: {clsStatus.current}</div>
              )}
            </div>
          )}

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>AVTO rejim</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Yoqilsa — kanalga kelgan har yangi post avtomatik AI'dan o'tib, mos bo'lsa FAQ ga qo'shiladi.
              </div>
            </div>
            <Switch
              checked={auto}
              onChange={toggleAuto}
              label={auto ? 'Yoqilgan' : "O'chirilgan"}
              aria-label="AVTO rejim"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        // Sahifaning asosiy qismi (statistika + postlar) yuklanmoqda — "working"
        <Loader state="working" size="lg" block />
      ) : (
        <>
          {/* Statistika */}
          {stats && (
            <div className="g4" style={{ marginBottom: 16 }}>
              <MiniStat label="Jami postlar" value={stats.total} />
              <MiniStat label="Videolar" value={stats.by_media?.video || 0} />
              <MiniStat label="FAQ da" value={`${stats.in_faq} / ${stats.total}`} />
              <MiniStat label="Davr" value={stats.first_date ? `${fmtDate(stats.first_date)} — ${fmtDate(stats.last_date)}` : '—'} small />
            </div>
          )}

          {/* Filtrlar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <input placeholder="Qidirish (matn bo'yicha)..." defaultValue={search} aria-label="Postlar ichidan qidirish"
              onChange={e => { clearTimeout(searchTimer.current); const v = e.target.value; searchTimer.current = setTimeout(() => { setPage(1); setSearch(v) }, 400) }}
              style={{ flex: '1 1 240px', padding: '8px 12px', fontSize: 13, borderRadius: 'var(--r)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-1)' }} />
            {['', 'video', 'photo', 'document'].map(m => (
              <button key={m || 'all'} onClick={() => { setPage(1); setMedia(m) }} aria-pressed={media === m}
                style={{ padding: '6px 12px', borderRadius: 'var(--r)', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${media === m ? 'var(--text-1)' : 'var(--border)'}`,
                  background: media === m ? 'var(--text-1)' : 'var(--surface)',
                  color: media === m ? 'var(--surface)' : 'var(--text-2)' }}>
                {m === '' ? 'Hammasi' : MEDIA_BADGE[m].label}
              </button>
            ))}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: 'var(--text-2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyUnfaq} onChange={e => { setPage(1); setOnlyUnfaq(e.target.checked) }} />
              Faqat FAQ da yo'qlari
            </label>
          </div>

          {/* Postlar */}
          {(!posts || posts.items.length === 0) ? (
            <EmptyState icon={HiCircleStack} title="Postlar yo'q"
              subtitle="«Yangi postlarni olish» tugmasi bilan kanal tarixini yig'ing (Telegram akkaunt ulangan bo'lishi kerak)." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {posts.items.map(p => {
                const B = MEDIA_BADGE[p.media_type] || MEDIA_BADGE.none
                return (
                  <div key={p.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: 14, background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 'var(--r)', background: 'var(--surface-2)', color: 'var(--text-2)' }}>
                        <B.icon size={12} aria-hidden="true" /> {B.label}
                        {p.media_meta?.duration ? ` · ${Math.floor(p.media_meta.duration / 60)}:${String(p.media_meta.duration % 60).padStart(2, '0')}` : ''}
                      </span>
                      <a href={p.link} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--mono)', color: 'var(--text-1)', textDecoration: 'none' }}>#{p.message_id}</a>
                      <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {fmtDate(p.date)} · <HiEye size={12} aria-hidden="true" /> {p.views}
                      </span>
                      <span style={{ marginLeft: 'auto' }} />
                      {p.is_faq ? (
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ui-st-ok)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><HiCheck size={13} aria-hidden="true" /> FAQ da</span>
                      ) : (
                        <button className="btn btn-primary btn-sm" onClick={() => openFaqModal(p)}><HiPlus size={13} aria-hidden="true" /> FAQ ga</button>
                      )}
                    </div>
                    {p.text && <div style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.5, whiteSpace: 'pre-wrap', maxHeight: 72, overflow: 'hidden' }}>{p.text}</div>}
                    {p.hashtags?.length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {p.hashtags.map(h => <span key={h} style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-2)', background: 'var(--surface-2)', padding: '2px 7px', borderRadius: 'var(--r)' }}>{h}</span>)}
                      </div>
                    )}
                  </div>
                )
              })}
              {/* Sahifalash */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 6, alignItems: 'center' }}>
                <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  title="Oldingi sahifa" aria-label="Oldingi sahifa"><HiChevronLeft aria-hidden="true" /></button>
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{page} / {totalPages} · {posts.total} ta</span>
                <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                  title="Keyingi sahifa" aria-label="Keyingi sahifa"><HiChevronRight aria-hidden="true" /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* FAQ ga qo'shish modali */}
      <Modal open={!!faqModal} onClose={() => setFaqModal(null)} title="Postni FAQ ga qo'shish"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setFaqModal(null)}>Bekor</button>
          <button className="btn btn-primary" onClick={saveFaq} disabled={saving || !question.trim()}>
            {saving ? <Spinner size={14} /> : <><HiCheck /> FAQ ga qo'shish</>}
          </button>
        </>}>
        {faqModal && (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
              Javob (havola): <b style={{ fontFamily: 'var(--mono)' }}>{faqModal.link}</b>
            </div>
            <div className="form-group">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <label className="form-label">Savol (fuqarolar qidiradigan so'zlar bilan) *</label>
                <button className="btn btn-secondary btn-sm" onClick={askAi} disabled={suggesting}>
                  {suggesting ? <Spinner size={12} /> : <><HiSparkles size={12} /> AI taklifi</>}
                </button>
              </div>
              <textarea className="form-input" rows={2} value={question} onChange={e => setQuestion(e.target.value)}
                placeholder="Masalan: Soxta treyding platformalari (treding, investitsiya, pul ishlash)" />
              {suggests.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {suggests.map((s, i) => (
                    <button key={i} onClick={() => setQuestion(s)}
                      style={{ textAlign: 'left', fontSize: 12, padding: '8px 12px', borderRadius: 'var(--r)', cursor: 'pointer',
                        border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-1)' }}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Kategoriya (ixtiyoriy)</label>
              <select className="form-input" value={catId} onChange={e => setCatId(e.target.value)}>
                <option value="">— Tanlanmagan —</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.icon ? c.icon + ' ' : ''}{c.name_uz}</option>)}
              </select>
            </div>
          </>
        )}
      </Modal>

      {/* AI saralashni boshlash — xavfsiz, lekin token sarflaydi */}
      <ConfirmModal
        open={askClassify}
        onClose={() => setAskClassify(false)}
        onConfirm={() => { setAskClassify(false); startClassify() }}
        title="AI saralashni boshlash"
        text="AI barcha ko'rilmagan postlarni bittalab tahlil qiladi: token sarflanadi va jarayon uzoq davom etishi mumkin. Uni istalgan payt to'xtatish mumkin."
        confirmLabel="Boshlash"
      />

      {/* To'liq qayta yuklash — ma'lumot o'chmaydi, lekin uzoq davom etadi */}
      <ConfirmModal
        open={askFullSync}
        onClose={() => setAskFullSync(false)}
        onConfirm={() => { setAskFullSync(false); startSync('full') }}
        title="To'liq qayta yuklash"
        text="Kanalning butun tarixi boshidan qayta o'qiladi. Bu uzoq davom etadi; mavjud postlar yangilanadi, o'chirilmaydi."
        confirmLabel="Qayta yuklash"
      />
    </div>
  )
}

function MiniStat({ label, value, small }) {
  return (
    <Card>
      <div className="card-body">
        <div style={{ fontSize: small ? 13 : 18, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{label}</div>
      </div>
    </Card>
  )
}

